import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";

import { ThemeProvider } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import { theme } from "../../../colorTheme";
import useFetching from "../../../utils/Fetching";
import Footer from "../../footer/Footer";
import Header from "../../header/Header";
import LoginPage from "./../LoginPage";
import DynamicComponent from "../../dynamic/DynamicComponent";
import { STEP_ICONS } from "../../dynamic/StepIcons";
import { useDocuments } from "../../../hooks/useDocuments";

export default function Onboarding() {
  const {
    receiveFurthestSteps,
    sendFurthestSteps,
    triggerSafetyStepsFinished,
    triggerPrivacyStepsFinished,
    getPdf,
    getAllSteps,
  } = useFetching();
  const [furthestCompletedStep, setFurthestCompletedStep] = useState(undefined);
  const [activeStep, setActiveStep] = useState(undefined);
  const [activeSubstep, setActiveSubstep] = useState(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const documents = useDocuments();

  const [steps, setSteps] = useState([]);

  useEffect(() => {
    async function receive() {
      if (furthestCompletedStep === undefined) {
        setIsLoading(true);
      } else {
        setIsLoading(false);
      }
      const result = await receiveFurthestSteps();
      setFurthestCompletedStep(result);
      setActiveStep(result.step);
      setActiveSubstep(result.substep);
      setIsLoading(false);
    }
    receive();
  }, [receiveFurthestSteps, setFurthestCompletedStep]);

  const cachePdfs = async () => {
    if (Object.keys(documents).length > 0) {
      Object.values(documents).forEach(async (document) => {
        if (document !== "CERTIFICATE") {
          await getPdf(document, "reload");
        }
      });
    }
  };

  const TRIGGERS = {
    cache: cachePdfs,
    safety: triggerSafetyStepsFinished,
    privacy: triggerPrivacyStepsFinished,
  };

  const handleSubstepCompleted = useCallback(
    (step, substep) => {
      sendFurthestSteps(step, substep);
      setFurthestCompletedStep((prevFurthest) => {
        const furthestStep = Math.max(step, prevFurthest.step);
        let furthestSubstep = 0;
        if (step <= prevFurthest.step) {
          furthestSubstep =
            furthestStep === step
              ? Math.max(substep, prevFurthest.substep)
              : prevFurthest.substep;
        }
        return {
          step: furthestStep,
          substep: furthestSubstep,
        };
      });
    },
    [setFurthestCompletedStep, sendFurthestSteps]
  );

  const substeps =
    steps.length > 0 &&
    typeof activeStep === "number" &&
    activeStep >= 0 &&
    steps[activeStep] &&
    Array.isArray(steps[activeStep].pages)
      ? steps[activeStep].pages.length
      : 0;

  const firstStep = activeStep === 0;
  const firstSubstep = activeSubstep === 0;
  const lastStep = activeStep === steps.length - 1;
  const lastSubstep = activeSubstep === substeps - 1;

  const stepForward = () => {
    setActiveSubstep(0);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const jumpTo = (step) => {
    if (step <= furthestCompletedStep.step || step === activeStep) {
      setActiveSubstep(0);
      setActiveStep(step);
    }
  };

  const stepBackward = () => {
    if (activeStep === 0) {
      return;
    }
    setActiveSubstep(0);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const substepForward = useCallback(() => {
    if (lastSubstep) {
      stepForward();
    } else {
      setActiveSubstep((prevActiveSubstep) => prevActiveSubstep + 1);
    }
  }, [activeSubstep, lastSubstep]);

  const substepBackward = () => {
    if (firstSubstep) {
      //Wenn man schon beim ersten Substep ist und man zurück drückt soll man auf die letzte Substep des vorherigen Steps kommen
      stepBackward();
      setActiveSubstep(steps[activeStep - 1].pages.length - 1);
    } else {
      setActiveSubstep((prevActiveSubstep) => prevActiveSubstep - 1);
    }
  };

  const isSubstepCompleted = useCallback(() => {
    if (
      activeStep === 0 &&
      activeSubstep === 0 &&
      furthestCompletedStep.step <= activeStep
    ) {
      return false;
    }
    if (
      furthestCompletedStep.step > activeStep || //activeStep ist immer ein Schritt zurück (?!)
      (furthestCompletedStep.step === activeStep &&
        furthestCompletedStep.substep >= activeSubstep + 1)
    ) {
      return true;
    }
    return false;
  }, [activeStep, activeSubstep, furthestCompletedStep]);

  const skipSubsteps = useCallback(
    (amount) => {
      if (activeSubstep >= substeps - (amount + 1)) {
        stepForward();
      } else {
        setActiveSubstep(
          (prevActiveSubstep) => prevActiveSubstep + (amount + 1)
        );
      }
    },
    [activeSubstep, substeps]
  );

  useEffect(() => {
    const mapRawDataSteps = (rawSteps) => {
      rawSteps.sort((a, b) => a.stepNumber - b.stepNumber);
      const mapped = rawSteps.map((step, stepIndex) => {
        step.pages.sort((a, b) => a.pageNumber - b.pageNumber);
        return {
          stepId: step.stepNumber,
          stepName: step.stepName,
          icon: (props) => STEP_ICONS[step.icon](props),
          pages: step.pages.map((page, pageIndex) => {
            const skip = page.props.skip ? page.props.skip : 0;
            return {
              component: (
                <DynamicComponent
                  key={`dynamic-component-page-${page.pageId}`}
                  component={page.component}
                  setCompleted={() => {
                    if (page.props.trigger) {
                      TRIGGERS[page.props.trigger]();
                    }
                    handleSubstepCompleted(stepIndex, pageIndex + skip);
                  }}
                  nextSubstep={() => substepForward()}
                  {...page.props}
                  skip={skip > 0 ? () => skipSubsteps(skip) : () => {}}
                />
              ),
            };
          }),
        };
      });
      return mapped;
    };

    async function getSteps() {
      const response = await getAllSteps();
      const mappedSteps = mapRawDataSteps(response);
      setSteps(mappedSteps);
    }

    getSteps();
  }, [
    getAllSteps,
    setSteps,
    handleSubstepCompleted,
    substepForward,
    skipSubsteps,
  ]);

  const isReady =
    !isLoading &&
    steps.length > 0 &&
    furthestCompletedStep !== undefined &&
    typeof activeStep === "number" &&
    activeStep >= 0 &&
    activeStep < steps.length &&
    typeof activeSubstep === "number" &&
    steps[activeStep] &&
    Array.isArray(steps[activeStep].pages) &&
    activeSubstep >= 0 &&
    activeSubstep < steps[activeStep].pages.length;

  return (
    <>
      <AuthenticatedTemplate>
        <ThemeProvider theme={theme}>
          {!isReady ? (
            // Lade-Spinner anzeigen solange noch nicht alles bereit
            <div className="container-fullscreen">
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div className="main" style={{ color: theme.palette.text.main }}>
              <Header
                steps={steps}
                activeStep={activeStep}
                jumpTo={jumpTo}
                furthestCompletedStep={furthestCompletedStep}
              />
              <div id="activePage">
                {steps[activeStep].pages[activeSubstep]?.component}
              </div>
              <Footer
                steps={substeps}
                activeSubstep={activeSubstep}
                setActiveSubstep={setActiveSubstep}
                firstStep={activeStep === 0}
                lastStep={activeStep === steps.length - 1}
                firstSubstep={activeSubstep === 0}
                lastSubstep={
                  activeSubstep === steps[activeStep].pages.length - 1
                }
                stepForward={stepForward}
                stepBackward={stepBackward}
                substepForward={substepForward}
                substepBackward={substepBackward}
                substepIsCompleted={isSubstepCompleted()}
                stepIsCompleted={furthestCompletedStep.step > activeStep}
              />
            </div>
          )}
        </ThemeProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
    </>
  );
}
